/**
 * Session 3B — shared API client for the 20 bespoke pages built in
 * PRs 1-20 (Attendance · EOS · Payroll · Travel second-line).
 *
 * Per the prompt's §0.1: frontend + backend ship in the SAME PR per
 * screen. This file is updated alongside each screen's PR so the
 * client wire-format stays in lockstep with the FastAPI response
 * model.
 *
 * All methods carry the Clerk JWT + tenant-slug headers via the
 * `authHeaders()` helper from `api.ts` (lesson learned from
 * Session 3a's missing-auth bug).
 */

import { apiBaseUrl, ApiError, authHeaders } from "./api";

async function _safeGet<T>(path: string, fallback: T): Promise<T> {
  let headers: Record<string, string>;
  try {
    headers = await authHeaders();
  } catch {
    headers = {};
  }
  headers["Content-Type"] = "application/json";
  try {
    const r = await fetch(`${apiBaseUrl}${path}`, { headers });
    if (!r.ok) {
      if (r.status === 404 || r.status === 501) return fallback;
      let detail = r.statusText;
      try {
        const body = await r.json();
        if (body && typeof body.detail === "string") detail = body.detail;
      } catch {
        // Non-JSON body — keep statusText.
      }
      throw new ApiError(r.status, detail);
    }
    return (await r.json()) as T;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Attendance (PRs 1-6)
// ---------------------------------------------------------------------------

export interface ShiftRosterItem {
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  position_title: string | null;
  shift_code: string | null;
  shift_name_en: string | null;
  days_of_week: number[];
  active_from: string;
  active_to: string | null;
  override_source: "default" | "custom";
  status: "active" | "ended" | "unassigned";
}

export interface OvertimeApprovalItem {
  ot_id: string;
  employee_id: string;
  employee_name_en: string;
  attendance_date: string;
  overtime_hours: number;
  article_107_premium_sar: number;
  basic_hourly_sar: number;
  state: "pending" | "approved" | "denied";
  approver_name: string | null;
  reason: string;
}

export interface AttendanceExceptionItem {
  exception_id: string;
  employee_id: string;
  employee_name_en: string;
  date: string;
  exception_type:
    | "missing_clock_in"
    | "missing_clock_out"
    | "anomaly_detected"
    | "biometric_failure"
    | "duplicate_punch";
  detected_at: string;
  reason: string;
  severity: "info" | "warning" | "critical";
  status: "open" | "resolved" | "false_positive";
}

export interface BulkAdjustmentRunItem {
  run_id: string;
  action_type:
    | "holiday_backfill"
    | "system_outage_recovery"
    | "multi_employee_adjustment"
    | "roll_forward_late";
  initiated_by_name: string;
  initiated_at: string;
  employees_count: number;
  status: "draft" | "applied" | "reverted";
  reason: string;
}

export interface BiometricDeviceItem {
  device_id: string;
  device_code: string;
  location_label: string;
  last_sync_at: string | null;
  sync_status: "healthy" | "degraded" | "offline";
  pending_records: number;
  last_failure_at: string | null;
  health_score_pct: number;
}

export interface WfhRegisterItem {
  record_id: string;
  employee_id: string;
  employee_name_en: string;
  date: string;
  wfh_status: "approved" | "pending" | "denied";
  geofence_match: boolean | null;
  approver_name: string | null;
  reason: string;
}

// ---------------------------------------------------------------------------
// EOS (PRs 7-10)
// ---------------------------------------------------------------------------

export interface EosCalculationDraftItem {
  draft_id: string;
  employee_id: string;
  employee_name_en: string;
  termination_date_scenario: string;
  reason_code_scenario: string;
  calculated_settlement_sar: number;
  saved_by_name: string;
  saved_at: string;
  notes: string | null;
}

export interface ClearanceCenterItem {
  termination_id: string;
  employee_id: string;
  employee_name_en: string;
  termination_date: string;
  items_total: number;
  items_complete: number;
  items_pending: number;
  pending_responsibility: string[];
  days_outstanding: number;
  status: "in_progress" | "complete" | "stuck";
}

export interface GovFilingsQueueItem {
  filing_id: string;
  termination_id: string;
  employee_id: string;
  employee_name_en: string;
  filing_type: "qiwa" | "gosi" | "iqama";
  drafted_at: string | null;
  status: "drafted" | "ready" | "submitted" | "accepted" | "rejected";
  submitted_at: string | null;
  days_since_drafted: number | null;
}

export interface FinalSettlementItem {
  settlement_id: string;
  termination_id: string;
  employee_id: string;
  employee_name_en: string;
  settlement_amount_sar: number;
  status: "draft" | "approved" | "paid" | "disputed";
  generated_at: string;
  paid_at: string | null;
  days_from_termination_to_payment: number | null;
}

// ---------------------------------------------------------------------------
// Payroll (PRs 11-17 — read-only, keystone-critical)
// ---------------------------------------------------------------------------

export interface PeriodLockEmployeeRow {
  employee_id: string;
  employee_name_en: string;
  gross_sar: number;
  deductions_sar: number;
  net_sar: number;
  variance_vs_prior_pct: number | null;
  status: "in_accumulator" | "ready_to_post" | "flagged_for_review";
}

export interface PeriodLockResponse {
  period_label: string;
  period_state: "open" | "locked" | "posted";
  employees_count: number;
  total_gross_sar: number;
  total_deductions_sar: number;
  total_net_sar: number;
  variance_vs_prior_pct: number | null;
  items: PeriodLockEmployeeRow[];
}

export interface OffCycleItem {
  disbursement_id: string;
  employee_id: string;
  employee_name_en: string;
  disbursement_type:
    | "travel_advance"
    | "loan_disbursement"
    | "ad_hoc_bonus"
    | "final_settlement"
    | "other";
  amount_sar: number;
  requested_at: string;
  state: "pending" | "approved" | "disbursed" | "denied";
  bank_status: "pending" | "submitted" | "confirmed" | "failed" | "na";
}

export interface PayslipDistributionItem {
  payslip_id: string;
  employee_id: string;
  employee_name_en: string;
  period_label: string;
  generated_at: string | null;
  delivered_via: "whatsapp" | "email" | "portal" | "pending";
  delivered_at: string | null;
  status: "generated" | "sent" | "delivered" | "failed";
  last_delivery_attempt_at: string | null;
}

export interface WpsRunItem {
  run_id: string;
  period_label: string;
  generated_at: string;
  sif_file_hash: string | null;
  employees_count: number;
  total_amount_sar: number;
  submission_status: "draft" | "submitted" | "accepted" | "rejected";
  submitted_at: string | null;
}

export interface GlJournalRunItem {
  run_id: string;
  period_label: string;
  generated_at: string;
  format: "d365" | "sap" | "csv";
  line_count: number;
  total_debits_sar: number;
  total_credits_sar: number;
  balanced: boolean;
}

export interface BankFileRunItem {
  run_id: string;
  period_label: string;
  bank: "riyad" | "alrajhi" | "snb" | "anb" | "albilad" | "other";
  generated_at: string;
  employees_count: number;
  total_amount_sar: number;
  file_hash: string | null;
  submission_status: "draft" | "submitted" | "confirmed";
}

export interface PostHistoryItem {
  period_label: string;
  posted_at: string;
  posted_by_name: string;
  employees_count: number;
  total_gross_sar: number;
  total_net_sar: number;
  variance_vs_prior_pct: number | null;
  status: "posted" | "reversed";
}

// ---------------------------------------------------------------------------
// Travel (PRs 18-20)
// ---------------------------------------------------------------------------

export interface ActiveTravelerItem {
  trip_id: string;
  employee_id: string;
  employee_name_en: string;
  destination: string;
  departure_date: string;
  expected_return_date: string;
  days_remaining: number;
  purpose: string;
  per_diem_status: "advanced" | "pending" | "na";
  emergency_contact_name: string | null;
}

export interface ReconciliationItem {
  trip_id: string;
  employee_id: string;
  employee_name_en: string;
  destination: string;
  return_date: string;
  advance_paid_sar: number;
  expenses_claimed_sar: number;
  variance_sar: number;
  variance_pct: number;
  days_since_return: number;
  status: "pending" | "approved" | "needs_documentation" | "recovery";
}

export interface PerDiemDisbursementItem {
  disbursement_id: string;
  trip_id: string;
  employee_id: string;
  employee_name_en: string;
  destination: string;
  per_diem_days: number;
  daily_rate_sar: number;
  total_advance_sar: number;
  disbursement_state: "pending" | "approved" | "disbursed";
  bank_status: "pending" | "submitted" | "confirmed";
}

// ---------------------------------------------------------------------------
// API client object
// ---------------------------------------------------------------------------

export const session3bApi = {
  // Attendance
  listShiftRoster: (legalEntityId: string, days = 7) =>
    _safeGet<{ items: ShiftRosterItem[]; as_of: string }>(
      `/api/attendance/shift-roster?legal_entity_id=${encodeURIComponent(legalEntityId)}&days=${days}`,
      { items: [], as_of: new Date().toISOString() },
    ),
  listOvertimeApprovals: (legalEntityId: string, state?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (state) qs.append("state", state);
    return _safeGet<{ items: OvertimeApprovalItem[] }>(
      `/api/attendance/overtime-approvals?${qs.toString()}`,
      { items: [] },
    );
  },
  listAttendanceExceptions: (legalEntityId: string) =>
    _safeGet<{ items: AttendanceExceptionItem[] }>(
      `/api/attendance/exceptions?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listBulkAdjustmentRuns: (legalEntityId: string) =>
    _safeGet<{ items: BulkAdjustmentRunItem[] }>(
      `/api/attendance/bulk-adjustments?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listBiometricDevices: (legalEntityId: string) =>
    _safeGet<{ items: BiometricDeviceItem[] }>(
      `/api/attendance/biometric-devices?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listWfhRegister: (legalEntityId: string, date?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (date) qs.append("date", date);
    return _safeGet<{ items: WfhRegisterItem[] }>(
      `/api/attendance/wfh-register?${qs.toString()}`,
      { items: [] },
    );
  },

  // EOS
  listEosCalculationDrafts: (legalEntityId: string) =>
    _safeGet<{ items: EosCalculationDraftItem[] }>(
      `/api/eos/calculation-drafts?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listClearanceCenter: (legalEntityId: string) =>
    _safeGet<{ items: ClearanceCenterItem[] }>(
      `/api/eos/clearance-center?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listGovFilingsQueue: (legalEntityId: string, state?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (state) qs.append("state", state);
    return _safeGet<{ items: GovFilingsQueueItem[] }>(
      `/api/eos/gov-filings?${qs.toString()}`,
      { items: [] },
    );
  },
  listFinalSettlements: (legalEntityId: string, state?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (state) qs.append("state", state);
    return _safeGet<{ items: FinalSettlementItem[] }>(
      `/api/eos/final-settlements?${qs.toString()}`,
      { items: [] },
    );
  },

  // Payroll
  getPeriodLock: (legalEntityId: string) =>
    _safeGet<PeriodLockResponse>(
      `/api/payroll/period-lock?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      {
        period_label: "—",
        period_state: "open",
        employees_count: 0,
        total_gross_sar: 0,
        total_deductions_sar: 0,
        total_net_sar: 0,
        variance_vs_prior_pct: null,
        items: [],
      },
    ),
  listOffCycle: (legalEntityId: string, state?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (state) qs.append("state", state);
    return _safeGet<{ items: OffCycleItem[] }>(
      `/api/payroll/off-cycle?${qs.toString()}`,
      { items: [] },
    );
  },
  listPayslipDistribution: (legalEntityId: string, period?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (period) qs.append("period", period);
    return _safeGet<{ items: PayslipDistributionItem[] }>(
      `/api/payroll/payslip-distribution?${qs.toString()}`,
      { items: [] },
    );
  },
  listWpsRuns: (legalEntityId: string) =>
    _safeGet<{ items: WpsRunItem[] }>(
      `/api/payroll/wps?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listGlOutputRuns: (legalEntityId: string) =>
    _safeGet<{ items: GlJournalRunItem[] }>(
      `/api/payroll/gl-output?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listBankFileRuns: (legalEntityId: string) =>
    _safeGet<{ items: BankFileRunItem[] }>(
      `/api/payroll/bank-files?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listPostHistory: (legalEntityId: string) =>
    _safeGet<{ items: PostHistoryItem[] }>(
      `/api/payroll/post-history?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),

  // Travel
  listActiveTravelers: (legalEntityId: string) =>
    _safeGet<{ items: ActiveTravelerItem[] }>(
      `/api/travel/active-travelers?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listReconciliation: (legalEntityId: string) =>
    _safeGet<{ items: ReconciliationItem[] }>(
      `/api/travel/reconciliation?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),
  listPerDiemDisbursements: (legalEntityId: string, state?: string) => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (state) qs.append("state", state);
    return _safeGet<{ items: PerDiemDisbursementItem[] }>(
      `/api/travel/per-diem-disbursements?${qs.toString()}`,
      { items: [] },
    );
  },
};
